// Ctrl+K — uma caixa para achar e fazer (modelo Intercom): contatos, cards e
// solicitações, páginas e ações ("Novo atendimento", "Pegar próximo"…). Dentro
// de uma conversa, as respostas rápidas vêm primeiro e vão para o campo de
// mensagem — a pessoa confere antes de enviar.
//
// As telas recebem os comandos por evento (`sophia:command`, ver
// `runCommand`); quem não está montado recebe pela flag guardada da página.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  ArrowRightLeft, Bolt, CalendarDays, ClipboardList, Grid3x3, Hand, Home, LayoutGrid, Loader2, MessageCircle,
  MessagesSquare, Moon, Plus, ShoppingCart, Sun, UserCheck, UserPlus, GraduationCap, BookOpen,
  Sparkles, Tag, AlarmClock, CheckSquare, SquareX, CheckCircle2, UserRoundPlus,
} from 'lucide-react';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from '../ui/command';
import { useAuth } from '../../hooks/useAuth';
import { ApiError } from '../../services/api';
import { attendancesService, type ApiSessionListItem } from '../../services/attendances';
import { TERMINAL_STATUSES } from '../tabs/atendimento/sessionAccess';
import { boardsService, type CardSearchHit } from '../../services/boards';
import { quickRepliesService, type QuickReply } from '../../services/quickRepliesService';
import { getActiveCardConversation, CONVERSATION_SHORTCUT_LABEL } from '../../lib/conversationBridge';
import { SECTOR_NAMES } from '../../pages/board-completo/config';
import { describeMacroActions, type MacroAction } from '../../lib/macros';

const CHAT_PATH = '/atendimento/conversas';

/** Evento que abre a paleta (ex.: clique no "Buscar…" do topo). */
export const OPEN_PALETTE_EVENT = 'sophia:open-command-palette';
/** Comandos para a tela aberta: `{ id, ...dados }`; quem tratar marca `handled`. */
export const COMMAND_EVENT = 'sophia:command';

export interface PaletteCommand {
  id: string;
  text?: string;
  /** insert-reply / macro-execute: título e ações da macro (resposta rápida com ações). */
  title?: string;
  actions?: MacroAction[];
  available?: boolean;
  /** resolve-now: só a conversa desta sessão (o Ctrl+Shift+Enter pode chegar depois de trocar de conversa). */
  sessionId?: number;
  /** bulk-count: quantas conversas estão marcadas na fila (-1 = sem ação em massa). */
  count?: number;
  handled?: boolean;
}

/** Dispara um comando para a tela aberta; devolve se alguém tratou. */
export function runCommand(command: PaletteCommand): boolean {
  return dispatchCommand(command).handled === true;
}

/** Como runCommand, mas devolve o comando como a tela deixou (ex.: bulk-count preenche `count`). */
export function dispatchCommand(command: PaletteCommand): PaletteCommand {
  const detail = { ...command, handled: false };
  window.dispatchEvent(new CustomEvent<PaletteCommand>(COMMAND_EVENT, { detail }));
  return detail;
}

export function onCommand(handler: (command: PaletteCommand) => boolean | void): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<PaletteCommand>).detail;
    if (handler(detail)) detail.handled = true;
  };
  window.addEventListener(COMMAND_EVENT, listener);
  return () => window.removeEventListener(COMMAND_EVENT, listener);
}

const PAGES: { label: string; path: string; icon: typeof Home; keywords?: string }[] = [
  { label: 'Início', path: '/inicio', icon: Home },
  { label: 'Atendimento', path: CHAT_PATH, icon: MessageCircle, keywords: 'conversas whatsapp painel' },
  { label: 'Captação', path: '/atendimento/captacao', icon: UserPlus, keywords: 'leads funil' },
  { label: 'Matrículas', path: '/atendimento/matriculas', icon: GraduationCap },
  { label: 'Solicitações', path: '/solicitacoes', icon: ClipboardList, keywords: 'pedidos protocolos' },
  { label: 'Setores', path: '/boards', icon: Grid3x3, keywords: 'boards quadros' },
  { label: 'Pedagógico', path: '/pedagogico', icon: BookOpen, keywords: 'alunos turmas notas' },
  { label: 'Calendário', path: '/calendario', icon: CalendarDays },
  { label: 'Loja — Venda', path: '/loja/venda', icon: ShoppingCart },
  { label: 'Chat da equipe', path: '/chat', icon: MessagesSquare },
];

function cardPath(hit: CardSearchHit): string {
  if (hit.board_type === 'triagem_central') return `/solicitacoes?card=${hit.id}`;
  return `/board/${hit.sector_key || hit.board_id}?card=${hit.id}`;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isCoordinator = user?.profile?.role === 'coordinator';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState<ApiSessionListItem[]>([]);
  const [cards, setCards] = useState<CardSearchHit[]>([]);
  // Conversas em que o termo aparece no TEXTO das mensagens (não no nome/telefone).
  const [messageHits, setMessageHits] = useState<ApiSessionListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [available, setAvailable] = useState<boolean | null>(null);
  // Conversas marcadas na fila (ação em massa); null fora do Atendimento.
  const [bulkCount, setBulkCount] = useState<number | null>(null);

  const onChat = location.pathname === CHAT_PATH;
  const boardSector = location.pathname.match(/^\/board\/([^/]+)/)?.[1] ?? null;

  // Ctrl+K / Cmd+K de qualquer tela; o "Buscar…" do topo também abre.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  // Ao abrir: respostas rápidas (na conversa) e o status Disponível/Ausente.
  useEffect(() => {
    if (!open) { setQuery(''); return; }
    if (onChat) quickRepliesService.list().then(setReplies).catch(() => setReplies([]));
    if (onChat) {
      const reply = dispatchCommand({ id: 'bulk-count', count: -1 });
      setBulkCount(reply.handled && (reply.count ?? -1) >= 0 ? reply.count! : null);
    } else setBulkCount(null);
    if (!isCoordinator) attendancesService.getMyStatus().then((r) => setAvailable(r.available)).catch(() => setAvailable(null));
  }, [open, onChat, isCoordinator]);

  // Busca remota (contatos e cards), com pausa curta enquanto digita.
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) { setContacts([]); setCards([]); setMessageHits([]); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      // Sem `status`: a busca atravessa abertas, encerradas e canceladas. A mais
      // recente de cada contato aparece (com o status) e abre exatamente ela.
      const contactsReq = attendancesService.listSessions({ search: q, page_size: '12', ordering: '-last_activity_at' })
        .then((r) => {
          const seen = new Set<string>();
          setContacts(r.results.filter((s) => {
            const key = s.contact_phone || `id:${s.id}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }).slice(0, 5));
        })
        .catch(() => setContacts([]));
      // Texto das mensagens: opt-in no backend (sem índice), então só com 3+
      // letras e página pequena. Some do resultado quem já veio pelo nome.
      const messagesReq = q.length >= 3
        ? attendancesService.listSessions({ search: q, search_messages: '1', page_size: '12', ordering: '-last_activity_at' })
          .then((r) => setMessageHits(r.results))
          .catch(() => setMessageHits([]))
        : Promise.resolve(setMessageHits([]));
      const cardsReq = q.length >= 3
        ? boardsService.searchCards(q).then((r) => setCards(r.results.slice(0, 5))).catch(() => setCards([]))
        : Promise.resolve(setCards([]));
      Promise.all([contactsReq, messagesReq, cardsReq]).finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  const go = useCallback((path: string) => { setOpen(false); navigate(path); }, [navigate]);

  const insertReply = (reply: QuickReply) => {
    setOpen(false);
    const ok = runCommand({ id: 'insert-reply', text: reply.content, title: reply.title, actions: reply.actions ?? [] });
    if (!ok) toast.info('Abra uma conversa de WhatsApp para usar a resposta rápida.');
  };

  const assign = () => {
    setOpen(false);
    if (!runCommand({ id: 'assign' })) toast.info('Abra uma conversa para assumir.');
  };

  const suggest = () => {
    setOpen(false);
    // Espera a paleta fechar: a sugestão vai para o campo de mensagem.
    setTimeout(() => {
      if (!runCommand({ id: 'suggest-reply' })) toast.info('Abra uma conversa de WhatsApp em que você pode responder.');
    }, 50);
  };

  const forward = () => {
    setOpen(false);
    // O "Encaminhar" do cabeçalho da conversa abre o seletor de setor.
    setTimeout(() => {
      const button = document.querySelector<HTMLButtonElement>('[data-command="forward-sector"]');
      if (!button) toast.info('Abra uma conversa para encaminhar.');
      else if (button.disabled) toast.info('Só quem assumiu ou um gestor pode encaminhar.');
      else button.click();
    }, 50);
  };

  const tagConversation = () => {
    setOpen(false);
    // Espera a paleta fechar: o seletor de etiquetas abre por cima da conversa.
    setTimeout(() => {
      if (!runCommand({ id: 'tag-conversation' })) toast.info('Abra uma conversa para etiquetar.');
    }, 50);
  };

  const snoozeConversation = () => {
    setOpen(false);
    // Espera a paleta fechar: o menu "Adiar" abre no cabeçalho da conversa.
    setTimeout(() => {
      if (!runCommand({ id: 'snooze-conversation' })) toast.info('Abra uma conversa sua (ou seja gestor) para adiar.');
    }, 50);
  };

  // Ação em massa: a barra da fila abre o mesmo menu (comandos bulk-*).
  const bulkCommand = (id: string) => {
    setOpen(false);
    setTimeout(() => runCommand({ id }), 50);
  };

  const newAttendance = () => {
    setOpen(false);
    if (onChat && runCommand({ id: 'new-attendance' })) return;
    try { sessionStorage.setItem('sophia:conversas:showNewModal', 'true'); } catch { /* ignora */ }
    navigate(CHAT_PATH);
  };

  const newCard = () => {
    setOpen(false);
    runCommand({ id: 'new-card' });
  };

  const takeNext = async () => {
    setOpen(false);
    // No Atendimento, o mesmo fluxo do botão da fila (abre em "Minhas", avisos).
    if (onChat && runCommand({ id: 'take-next' })) return;
    try {
      const session = await attendancesService.takeNext();
      navigate(`${CHAT_PATH}?focusSessionId=${session.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) toast.info('Nenhum atendimento esperando na fila.');
      else toast.error('Não foi possível pegar o próximo atendimento.');
    }
  };

  const toggleAvailability = async () => {
    setOpen(false);
    if (available === null) return;
    try {
      const r = await attendancesService.setMyStatus(!available);
      setAvailable(r.available);
      runCommand({ id: 'availability-changed', available: r.available });
      toast.success(r.available ? 'Você está disponível para novos atendimentos.' : 'Você está ausente.');
    } catch {
      toast.error('Não foi possível mudar seu status.');
    }
  };

  // Achados só pelo texto das mensagens (os de nome/telefone já estão em Contatos).
  const messageOnlyHits = useMemo(() => {
    const shown = new Set(contacts.map((c) => c.id));
    return messageHits.filter((s) => !shown.has(s.id)).slice(0, 5);
  }, [contacts, messageHits]);

  const cardConversation = open ? getActiveCardConversation() : null;
  const pages = useMemo(() => PAGES.filter((p) => p.path !== location.pathname), [location.pathname]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Buscar ou executar" description="Contatos, cards, páginas e ações">
      <CommandInput value={query} onValueChange={setQuery} placeholder="Buscar contato, protocolo, página ou ação…" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>{searching ? 'Buscando…' : 'Nada encontrado.'}</CommandEmpty>

        {onChat && replies.length > 0 && (
          <CommandGroup heading="Respostas rápidas">
            {replies.map((r) => (
              <CommandItem key={r.id} value={`resposta ${r.title} ${r.content}`} onSelect={() => insertReply(r)}>
                <Bolt />
                <span className="truncate">{r.title}</span>
                {r.actions?.length ? (
                  <span data-testid="palette-macro-badge" className="ml-auto flex-shrink-0 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                    {describeMacroActions(r.actions)}
                  </span>
                ) : (
                  <span className="ml-auto truncate max-w-[45%] text-xs text-muted-foreground">{r.content}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {onChat && (
          <CommandGroup heading="Esta conversa">
            {!isCoordinator && (
              <CommandItem value="assumir atendimento" onSelect={assign}><UserCheck />Assumir atendimento<CommandShortcut>A</CommandShortcut></CommandItem>
            )}
            {!isCoordinator && (
              <CommandItem value="sugerir resposta ia" onSelect={suggest}><Sparkles />Sugerir resposta (IA)<CommandShortcut>G</CommandShortcut></CommandItem>
            )}
            <CommandItem value="encaminhar para setor" onSelect={forward}><ArrowRightLeft />Encaminhar para setor…<CommandShortcut>F</CommandShortcut></CommandItem>
            {!isCoordinator && (
              <CommandItem value="etiquetar conversa etiqueta assunto" onSelect={tagConversation}><Tag />Etiquetar conversa…<CommandShortcut>T</CommandShortcut></CommandItem>
            )}
            {!isCoordinator && (
              <CommandItem value="adiar conversa snooze lembrar depois" onSelect={snoozeConversation}><AlarmClock />Adiar conversa…<CommandShortcut>Z</CommandShortcut></CommandItem>
            )}
            {bulkCount !== null && (
              <CommandItem value="selecionar todas desta visão ação em massa" onSelect={() => bulkCommand('bulk-select-all')}>
                <CheckSquare />Selecionar todas desta visão
              </CommandItem>
            )}
          </CommandGroup>
        )}

        {bulkCount !== null && bulkCount > 0 && (
          <CommandGroup heading={`${bulkCount} ${bulkCount === 1 ? 'conversa selecionada' : 'conversas selecionadas'}`}>
            <CommandItem value="assumir selecionadas massa" onSelect={() => bulkCommand('bulk-assign-me')}><UserCheck />Assumir selecionadas</CommandItem>
            <CommandItem value="atribuir selecionadas devolver fila massa" onSelect={() => bulkCommand('bulk-assign')}><UserRoundPlus />Atribuir selecionadas…</CommandItem>
            <CommandItem value="etiquetar selecionadas etiqueta massa" onSelect={() => bulkCommand('bulk-tag')}><Tag />Etiquetar selecionadas…</CommandItem>
            <CommandItem value="adiar selecionadas snooze massa" onSelect={() => bulkCommand('bulk-snooze')}><AlarmClock />Adiar selecionadas…</CommandItem>
            <CommandItem value="encaminhar selecionadas setor massa" onSelect={() => bulkCommand('bulk-forward')}><ArrowRightLeft />Encaminhar selecionadas…</CommandItem>
            <CommandItem value="encerrar selecionadas massa" onSelect={() => bulkCommand('bulk-close')}><CheckCircle2 />Encerrar selecionadas…</CommandItem>
            <CommandItem value="limpar seleção desmarcar" onSelect={() => bulkCommand('bulk-clear')}><SquareX />Limpar seleção</CommandItem>
          </CommandGroup>
        )}

        {contacts.length > 0 && (
          <CommandGroup heading="Contatos">
            {contacts.map((s) => (
              <CommandItem
                key={s.id}
                value={`contato ${s.contact_name} ${s.contact_phone} ${s.protocol_number ?? ''} ${query}`}
                onSelect={() => go(`${CHAT_PATH}?focusSessionId=${s.id}`)}
              >
                <MessageCircle />
                <span className="truncate">{s.contact_name || s.contact_phone}</span>
                <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                  {TERMINAL_STATUSES.includes(s.status) && (
                    <span data-testid="hit-status" className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                      {s.status === 'cancelled' ? 'Cancelada' : 'Encerrada'}
                    </span>
                  )}
                  {s.contact_phone}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {messageOnlyHits.length > 0 && (
          <CommandGroup heading="Nas mensagens">
            {messageOnlyHits.map((s) => (
              <CommandItem
                key={s.id}
                value={`mensagem ${s.contact_name} ${s.contact_phone} ${s.id} ${query}`}
                onSelect={() => go(`${CHAT_PATH}?focusSessionId=${s.id}`)}
              >
                <MessagesSquare />
                <span className="truncate">{s.contact_name || s.contact_phone}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {TERMINAL_STATUSES.includes(s.status) ? (s.status === 'cancelled' ? 'Cancelada' : 'Encerrada') : s.status_display}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {cards.length > 0 && (
          <CommandGroup heading="Cards e solicitações">
            {cards.map((c) => (
              <CommandItem key={c.id} value={`card ${c.title} ${c.board_title} ${query}`} onSelect={() => go(cardPath(c))}>
                <LayoutGrid />
                <span className="truncate">{c.title}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {c.board_type === 'triagem_central' ? 'Solicitações' : (SECTOR_NAMES[c.sector_key ?? ''] ?? c.board_title)}
                  {c.is_archived ? ' · arquivado' : ''}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {searching && contacts.length === 0 && cards.length === 0 && messageOnlyHits.length === 0 && query.trim().length >= 2 && (
          <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" /> Buscando…</div>
        )}

        <CommandGroup heading="Ações">
          {cardConversation && (
            <CommandItem value="abrir conversa do card" onSelect={() => { setOpen(false); cardConversation(); }}>
              <MessageCircle />Abrir a conversa deste card<CommandShortcut>{CONVERSATION_SHORTCUT_LABEL}</CommandShortcut>
            </CommandItem>
          )}
          <CommandItem value="novo atendimento" onSelect={newAttendance}><Plus />Novo atendimento</CommandItem>
          {boardSector && (
            <CommandItem value="novo card neste board" onSelect={newCard}>
              <Plus />Novo card<span className="ml-auto text-xs text-muted-foreground">{SECTOR_NAMES[boardSector] ?? boardSector}</span>
            </CommandItem>
          )}
          {!isCoordinator && (
            <CommandItem value="pegar próximo da fila" onSelect={takeNext}><Hand />Pegar próximo da fila<CommandShortcut>P</CommandShortcut></CommandItem>
          )}
          {!isCoordinator && available !== null && (
            <CommandItem value={available ? 'ficar ausente' : 'ficar disponível'} onSelect={toggleAvailability}>
              {available ? <Moon /> : <Sun />}{available ? 'Ficar ausente' : 'Ficar disponível'}
            </CommandItem>
          )}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Ir para">
          {pages.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem key={p.path} value={`ir ${p.label} ${p.keywords ?? ''}`} onSelect={() => go(p.path)}>
                <Icon />{p.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
      <div className="flex gap-4 border-t px-4 py-2 text-[11px] text-muted-foreground">
        <span>↑↓ navegar</span><span>Enter abrir</span><span>Esc fechar</span><span className="ml-auto">Ctrl+K</span>
      </div>
    </CommandDialog>
  );
}
